import { useCallback } from "react";

import {
  BackButton,
  CancelButton,
  CompleteButton,
  Wizard,
  type ModalDialogProps,
  type WizardProps,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useFilterSettings } from "./FilterSettingsContext";
import { FilterTypePage, TagsPage } from "./pages";

type Props = ModalDialogProps &
  Pick<WizardProps, "onBegin" | "onCancel" | "onComplete" | "contentStyle" | "stepperStyle" | "errorMessage">;

const buttonStyle = { borderRadius: "8px" };

export const FilterWizard = ({ onCancel, ...rest }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { type, tags, reset } = useFilterSettings();

  const handleCancel = useCallback(() => {
    reset();
    onCancel();
  }, [onCancel, reset]);

  return (
    <Wizard
      {...rest}
      pages={[
        {
          id: "type",
          content: <FilterTypePage />,
          stepper: {
            back: <CancelButton style={buttonStyle} onClick={reset} />,
            next: <CompleteButton style={buttonStyle} busy={false} label={formatMessage("next")} />,
            useBackAsCancel: true,
            advanceCondition: () => !!type,
          },
        },
        {
          id: "tags",
          content: <TagsPage />,
          stepper: {
            back: !!rest.onBegin ? <CancelButton style={buttonStyle} /> : <BackButton style={buttonStyle} />,
            next: <CompleteButton style={buttonStyle} busy={false} label={formatMessage("apply")} />,
            useBackAsCancel: !!rest.onBegin,
            advanceCondition: () => !!tags.length,
          },
          showCondition: () => type === "tags",
        },
      ]}
      slotProps={{ paper: { sx: { borderRadius: "14px" } } }}
      stepPanel="horizontal"
      onCancel={handleCancel}
    />
  );
};

const formatStrings = {
  apply: {
    en: "Apply",
  },
  next: {
    en: "Next",
  },
} satisfies FormatMessages;
